import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, Car, Shield, Calculator, Check, Phone, Mail } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { useLanguage, Language } from "@/hooks/use-language";
import { getTranslation, TranslationKey } from "@/lib/translations";
import { Link } from "react-router-dom";

// Pricing data
const companyPricing = [
  { serviceKey: "companyServiceRegisterLLC" as TranslationKey, price: { value: 1500, prefix: "priceFrom" as TranslationKey, suffix: "" } },
  { serviceKey: "companyServiceRegisterSoleTrader" as TranslationKey, price: { value: 500, prefix: "priceFrom" as TranslationKey, suffix: "" } },
  { serviceKey: "companyServiceKRSChanges" as TranslationKey, price: { value: 800, prefix: "priceFrom" as TranslationKey, suffix: "" } },
  { serviceKey: "companyServiceVirtualAddress" as TranslationKey, price: { value: 250, prefix: "" as TranslationKey, suffix: "pricePerMonth" as TranslationKey } },
  { serviceKey: "companyServiceConsultation" as TranslationKey, price: { value: "", prefix: "" as TranslationKey, suffix: "" as TranslationKey, translationKey: "priceIndividual" as TranslationKey } },
];

const autoPricing = [
  { serviceKey: "autoServiceRegisterEU" as TranslationKey, price: { value: 800, prefix: "priceFrom" as TranslationKey, suffix: "" as TranslationKey } },
  { serviceKey: "autoServiceRegisterNonEU" as TranslationKey, price: { value: 1200, prefix: "priceFrom" as TranslationKey, suffix: "" as TranslationKey } },
  { serviceKey: "autoServiceInsuranceOC" as TranslationKey, price: { value: "", prefix: "" as TranslationKey, suffix: "" as TranslationKey, translationKey: "priceIndividual" as TranslationKey } },
  { serviceKey: "autoServiceInsuranceAC" as TranslationKey, price: { value: "", prefix: "" as TranslationKey, suffix: "" as TranslationKey, translationKey: "priceIndividual" as TranslationKey } },
  { serviceKey: "autoServiceDeregister" as TranslationKey, price: { value: 300, prefix: "priceFrom" as TranslationKey, suffix: "" as TranslationKey } },
];

const supportPricing = [
  { serviceKey: "supportServiceLegalization" as TranslationKey, price: { value: 1000, prefix: "priceFrom" as TranslationKey, suffix: "" as TranslationKey } },
  { serviceKey: "supportServicePESEL" as TranslationKey, price: { value: 400, prefix: "priceFrom" as TranslationKey, suffix: "" as TranslationKey } },
  { serviceKey: "supportServiceRepresentation" as TranslationKey, price: { value: 200, prefix: "" as TranslationKey, suffix: "" as TranslationKey } },
  { serviceKey: "supportServiceTranslation" as TranslationKey, price: { value: 60, prefix: "priceFrom" as TranslationKey, suffix: "" as TranslationKey } },
];

const formatPrice = (price: { value: number | string, prefix: string, suffix: string, translationKey?: string }, language: Language) => {
  if (price.translationKey) {
    return getTranslation(price.translationKey as TranslationKey, language);
  }
  
  const prefix = price.prefix ? `${getTranslation(price.prefix as TranslationKey, language)} ` : '';
  const suffix = price.suffix ? ` ${getTranslation(price.suffix as TranslationKey, language)}` : '';
  
  return `${prefix}${price.value}${price.value ? ' zł' : ''}${suffix}`;
};

// Accounting packages with translations
const getTaxPricing = (language: Language) => [
  { 
    titleKey: "taxPackageBasic" as TranslationKey, 
    price: { value: 350, prefix: "", suffix: "pricePerMonth" as TranslationKey }, 
    features: [
      "taxFeatureKPIR" as TranslationKey,
      "taxFeatureDocuments50" as TranslationKey,
      "taxFeatureTaxDeclarations" as TranslationKey,
      "taxFeatureZUSDeclarations" as TranslationKey,
      "taxFeatureEmailPhone" as TranslationKey
    ] 
  },
  { 
    titleKey: "taxPackageStandard" as TranslationKey, 
    price: { value: 650, prefix: "", suffix: "pricePerMonth" as TranslationKey }, 
    features: [
      "taxFeatureFullAccounting" as TranslationKey,
      "taxFeatureDocuments100" as TranslationKey,
      "taxFeatureTaxDeclarations" as TranslationKey,
      "taxFeatureZUSDeclarations" as TranslationKey,
      "taxFeatureBasicAdvice" as TranslationKey,
      "taxFeatureDedicatedAccountant" as TranslationKey
    ],
    recommended: true
  },
  { 
    titleKey: "taxPackagePremium" as TranslationKey, 
    price: { value: 1200, prefix: "", suffix: "pricePerMonth" as TranslationKey }, 
    features: [
      "taxFeatureFullAccounting" as TranslationKey,
      "taxFeatureUnlimitedDocuments" as TranslationKey,
      "taxFeatureAllDeclarations" as TranslationKey,
      "taxFeaturePayroll" as TranslationKey,
      "taxFeatureComprehensiveAdvice" as TranslationKey,
      "taxFeaturePrioritySupport" as TranslationKey,
      "taxFeatureDedicatedChiefAccountant" as TranslationKey
    ] 
  },
];

export default function PricingPage() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("company");
  const [activePackage, setActivePackage] = useState(0); // 0: Basic, 1: Standard, 2: Premium
  const [taxServiceTypeTab, setTaxServiceTypeTab] = useState("accounting"); // "accounting" or "tax"
  const taxPricing = getTaxPricing(language);

  const getTranslatedService = (serviceKey: TranslationKey) => {
    return getTranslation(serviceKey, language);
  };

  // Handle tax service type tab changes
  const handleTaxServiceTypeChange = (tab: string) => {
    setTaxServiceTypeTab(tab);
    // Keep the same active package index (e.g., if viewing BASIC package in accounting, show BASIC in tax)
    // This creates a smoother transition between tax and accounting tabs
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-muted/30 py-12 md:py-16">
        <div className="container">
          <BackButton className="mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{getTranslation('pricing' as TranslationKey, language)}</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            {getTranslation('pricingDescription' as TranslationKey, language)}
          </p>
        </div>
      </div>
      
      {/* Pricing Content */}
      <div className="container py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span className="hidden md:inline">{getTranslation('company' as TranslationKey, language)}</span>
            </TabsTrigger>
            <TabsTrigger value="auto" className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              <span className="hidden md:inline">{getTranslation('auto' as TranslationKey, language)}</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden md:inline">{getTranslation('support' as TranslationKey, language)}</span>
            </TabsTrigger>
            <TabsTrigger value="tax" className="flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              <span className="hidden md:inline">{getTranslation('accounting' as TranslationKey, language)}</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="mt-8">
            <TabsContent value="company">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Briefcase className="h-6 w-6 text-primary" />
                {getTranslation('companyServicesTitle' as TranslationKey, language)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {companyPricing.map((item, index) => (
                  <Card key={index} className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-t-3 border-t-primary">
                    <CardHeader>
                      <CardTitle className="text-xl">{getTranslatedService(item.serviceKey)}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">{formatPrice(item.price, language)}</p>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3">
                      <Button asChild variant="default" className="w-full sm:w-auto">
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="w-full sm:w-auto">
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="auto">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Car className="h-6 w-6 text-primary" />
                {getTranslation('autoServicesTitle' as TranslationKey, language)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {autoPricing.map((item, index) => (
                  <Card key={index} className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-t-3 border-t-primary">
                    <CardHeader>
                      <CardTitle className="text-xl">{getTranslatedService(item.serviceKey)}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">{formatPrice(item.price, language)}</p>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3">
                      <Button asChild variant="default" className="w-full sm:w-auto">
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="w-full sm:w-auto">
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="support">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                {getTranslation('supportServicesTitle' as TranslationKey, language)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {supportPricing.map((item, index) => (
                  <Card key={index} className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-t-3 border-t-primary">
                    <CardHeader>
                      <CardTitle className="text-xl">{getTranslatedService(item.serviceKey)}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold text-primary">{formatPrice(item.price, language)}</p>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3">
                      <Button asChild variant="default" className="w-full sm:w-auto">
                        <a href="tel:+48886695593">
                          <Phone className="mr-2 h-4 w-4" />
                          {getTranslation("quickContact", language)}
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="w-full sm:w-auto">
                        <a href="mailto:kontakt1group.pl@gmail.com">
                          <Mail className="mr-2 h-4 w-4" />
                          {getTranslation("emailContact", language)}
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="tax">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Calculator className="h-6 w-6 text-primary" />
                {getTranslation('accountingPackagesTitle' as TranslationKey, language)}
              </h2>
              
              {/* Tax/Accounting Service Type Selection */}
              <div className="mb-8">
                <Tabs 
                  value={taxServiceTypeTab} 
                  onValueChange={handleTaxServiceTypeChange} 
                  className="w-full"
                >
                  <TabsList className="flex flex-wrap w-full border rounded-lg p-1 bg-muted/20 max-w-md mx-auto mb-6">
                    <TabsTrigger
                      value="accounting"
                      className="flex-1 py-3"
                    >
                      {getTranslation("accounting", language)}
                    </TabsTrigger>
                    <TabsTrigger
                      value="tax"
                      className="flex-1 py-3"
                    >
                      {getTranslation("tax", language)}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Package Switcher */}
              <div className="mb-8">
                <Tabs value={activePackage.toString()} onValueChange={(value) => setActivePackage(parseInt(value))} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto">
                    <TabsTrigger value="0" className="text-sm md:text-base">
                      {getTranslation("basicPackage", language)}
                    </TabsTrigger>
                    <TabsTrigger value="1" className="text-sm md:text-base">
                      {getTranslation("standardPackage", language)}
                    </TabsTrigger>
                    <TabsTrigger value="2" className="text-sm md:text-base">
                      {getTranslation("premiumPackage", language)}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              <div className="grid grid-cols-1 gap-6">
                {/* Display only the selected package */}
                <Card 
                  className={`group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 max-w-3xl mx-auto ${taxPricing[activePackage].recommended ? 'border-primary border-2 shadow-md' : 'border-t-3 border-t-primary'}`}
                >
                  {taxPricing[activePackage].recommended && (
                    <div className="bg-primary text-primary-foreground py-2 px-4 text-center text-sm font-semibold">
                      {getTranslation('labelRecommended' as TranslationKey, language)}
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-2xl text-center">
                      {taxServiceTypeTab === "accounting" 
                        ? getTranslation(taxPricing[activePackage].titleKey, language)
                        : `${getTranslation("tax", language)} - ${getTranslation(taxPricing[activePackage].titleKey, language)}`}
                    </CardTitle>
                    <CardDescription className="text-center">
                      <span className="text-3xl font-bold text-primary">{formatPrice(taxPricing[activePackage].price, language)}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {taxServiceTypeTab === "accounting" ? (
                        // Display accounting features
                        taxPricing[activePackage].features.map((featureKey, i) => (
                          <li key={i} className="flex items-center gap-3">
                            <Check className="h-5 w-5 text-primary flex-shrink-0" />
                            <span>{getTranslation(featureKey, language)}</span>
                          </li>
                        ))
                      ) : (
                        // Display tax features
                        activePackage === 0 ? (
                          // Basic tax features
                          [
                            "Optymalizacja podatkowa",
                            "Analiza obciążeń podatkowych",
                            "Doradztwo w zakresie VAT",
                            "Planowanie zobowiązań podatkowych",
                            "Przegląd dokumentacji pod kątem podatkowym",
                            "Kwartalny raport podatkowy"
                          ].map((feature, i) => (
                            <li key={i} className="flex items-center gap-3">
                              <Check className="h-5 w-5 text-primary flex-shrink-0" />
                              <span>{language === 'pl' ? feature :
                                     language === 'uk' ? [
                                       "Податкова оптимізація",
                                       "Аналіз податкових навантажень",
                                       "Консультації щодо ПДВ",
                                       "Планування податкових зобов'язань",
                                       "Перегляд документації з податкової точки зору",
                                       "Щоквартальний податковий звіт"
                                     ][i] :
                                     language === 'ru' ? [
                                       "Налоговая оптимизация",
                                       "Анализ налоговых нагрузок",
                                       "Консультации по НДС",
                                       "Планирование налоговых обязательств",
                                       "Просмотр документации с налоговой точки зрения",
                                       "Ежеквартальный налоговый отчет"
                                     ][i] : [
                                       "Tax optimization",
                                       "Analysis of tax burdens",
                                       "VAT advisory",
                                       "Tax liability planning",
                                       "Document review for tax purposes",
                                       "Quarterly tax report"
                                     ][i]}</span>
                            </li>
                          ))
                        ) : activePackage === 1 ? (
                          // Standard tax features
                          [
                            "Pełne doradztwo podatkowe",
                            "Optymalizacja kosztów podatkowych",
                            "Planowanie podatkowe",
                            "Opinie podatkowe",
                            "Analiza umów pod kątem podatkowym",
                            "Miesięczny raport podatkowy"
                          ].map((feature, i) => (
                            <li key={i} className="flex items-center gap-3">
                              <Check className="h-5 w-5 text-primary flex-shrink-0" />
                              <span>{language === 'pl' ? feature :
                                     language === 'uk' ? [
                                       "Повне податкове консультування",
                                       "Оптимізація податкових витрат",
                                       "Податкове планування",
                                       "Податкові висновки",
                                       "Аналіз договорів з податкової точки зору",
                                       "Щомісячний податковий звіт"
                                     ][i] :
                                     language === 'ru' ? [
                                       "Полное налоговое консультирование",
                                       "Оптимизация налоговых расходов",
                                       "Налоговое планирование",
                                       "Налоговые заключения",
                                       "Анализ договоров с налоговой точки зрения",
                                       "Ежемесячный налоговый отчет"
                                     ][i] : [
                                       "Full tax advisory",
                                       "Optimization of tax costs",
                                       "Tax planning",
                                       "Tax opinions",
                                       "Contract analysis for tax purposes",
                                       "Monthly tax report"
                                     ][i]}</span>
                            </li>
                          ))
                        ) : (
                          // Premium tax features
                          [
                            "Kompleksowe doradztwo podatkowe",
                            "Nieograniczona liczba konsultacji",
                            "Międzynarodowe planowanie podatkowe",
                            "Reprezentacja przed organami podatkowymi",
                            "Audyt podatkowy",
                            "Wsparcie podczas kontroli skarbowej",
                            "Indywidualne interpretacje podatkowe",
                            "Dedykowany doradca podatkowy"
                          ].map((feature, i) => (
                            <li key={i} className="flex items-center gap-3">
                              <Check className="h-5 w-5 text-primary flex-shrink-0" />
                              <span>{language === 'pl' ? feature :
                                     language === 'uk' ? [
                                       "Комплексні податкові консультації",
                                       "Необмежена кількість консультацій",
                                       "Міжнародне податкове планування",
                                       "Представництво перед податковими органами",
                                       "Податковий аудит",
                                       "Підтримка під час податкових перевірок",
                                       "Індивідуальні податкові інтерпретації",
                                       "Персональний податковий консультант"
                                     ][i] :
                                     language === 'ru' ? [
                                       "Комплексные налоговые консультации",
                                       "Неограниченное количество консультаций",
                                       "Международное налоговое планирование",
                                       "Представительство перед налоговыми органами",
                                       "Налоговый аудит",
                                       "Поддержка во время налоговых проверок",
                                       "Индивидуальные налоговые интерпретации",
                                       "Персональный налоговый консультант"
                                     ][i] : [
                                       "Comprehensive tax advisory",
                                       "Unlimited consultations",
                                       "International tax planning",
                                       "Representation before tax authorities",
                                       "Tax audit",
                                       "Support during tax inspections",
                                       "Individual tax interpretations",
                                       "Dedicated tax advisor"
                                     ][i]}</span>
                            </li>
                          ))
                        )
                      )}
                    </ul>
                  </CardContent>
                  <CardFooter className="flex flex-col sm:flex-row gap-3">
                    <Button asChild variant="default" className="w-full sm:w-auto">
                      <a href="tel:+48886695593">
                        <Phone className="mr-2 h-4 w-4" />
                        {getTranslation("quickContact", language)}
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="w-full sm:w-auto">
                      <a href="mailto:kontakt1group.pl@gmail.com">
                        <Mail className="mr-2 h-4 w-4" />
                        {getTranslation("emailContact", language)}
                      </a>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
              
              <div className="mt-8 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  {getTranslation('noteVAT' as TranslationKey, language)}
                  <br />
                  {getTranslation('noteFinalPrice' as TranslationKey, language)}
                </p>
              </div>
            </TabsContent>
          </div>
        </Tabs>
        
        {/* CTA Section */}
        <div className="mt-16 py-10 px-6 bg-gradient-to-br from-primary/20 to-background rounded-xl text-center">
          <h3 className="text-2xl font-semibold mb-4">{getTranslation('ctaTitle' as TranslationKey, language)}</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            {getTranslation('ctaDescription' as TranslationKey, language)}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="flex items-center gap-2">
              <a href="tel:+48886695593">
                <Phone className="h-5 w-5" />
                {getTranslation("quickContact", language)}
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="flex items-center gap-2">
              <a href="mailto:kontakt1group.pl@gmail.com">
                <Mail className="h-5 w-5" />
                {getTranslation("emailContact", language)}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}